/* eslint-disable @typescript-eslint/no-use-before-define */
import { ArrowLeftIcon } from '@eleveo/icons-react';
import { Button, Col, Container, Icon as SharedIcon, Row } from '@zoom/components';
import { areIntervalsOverlapping } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { FormApi } from 'final-form';
import { equals, isEmpty } from 'ramda';
import React, { useEffect, useState } from 'react';
import { Field, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Form } from 'reactstrap';

import { AdjustmentsPanel } from './AdjustmentsPanel';
import { AdjustmentRangeType } from './Chart';
import { ForecastAdjustmentsSidebar } from './ForecastAdjustmentsSidebar';
import { ForecastingDropdownTimezone } from './ForecastingDropdownTimezone';
import { FormConfigureForecastCharts } from './FormConfigureForecastCharts';
import { DatePickerField } from '../../../components/DatePicker';
import Icon from '../../../components/Icon';
import { ProductionIcon } from '../../../components/ListIcons/ProductionIcon';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { PromptModal } from '../../../components/Prompt/PromptModal';
import { HeaderProps } from '../../../components/layout/CommonHeader';
import { Content } from '../../../components/layout/Content';
import { Separator } from '../../../components/layout/Separator';
import { useSidebar } from '../../../components/layout/Sidebar';
import { CalculatedForecastData } from '../../../graphql/components';
import { PATH_SCHEDULING_STUDIO_CREATE } from '../../../router/routes';
import { CreateEditViewType as Action, RecordBooleanType } from '../../../types';
import { createEndOfDateRange } from '../../../utils/dateRange';
import { Adjustment, AdjustmentChange, AdjustmentChangeType, adjustmentInChangeLabelKey } from '../config';
import {
  ADJUSTMENTS_SIDEBAR_TOGGLE_BUTTON,
  CANCEL_BUTTON,
  DATE_RANGE_END_DATE,
  DATE_RANGE_LABEL,
  DATE_RANGE_START_DATE,
  PREVIOUS_STEP_BUTTON,
  PROCEED_TO_SCHEDULE_BUTTON,
  PUT_TO_PRODUCTION_TOGGLE_BUTTON,
  SAVE_BUTTON,
  SKIP_SWITCH_TO_EDIT,
  getButtonTestId,
} from '../constants';
import { handleRangeCollisionsWithinAdjustments } from '../utils';
import { getHeaderData } from '../utils/header';
import { FormValues } from '../utils/values';

export type CommonFormConfigureForecastProps = FormRenderProps<FormValues> & {
  action: Action;
  adjustments: Adjustment[];
  createForecastAndPutToProduction: (form: FormApi<FormValues>, values: FormValues) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  forecastCreatedUpdated: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  initializeAdjustments: (adjustments?: Adjustment[]) => void;
  isEdit: boolean;
  isProduction: boolean;
  isView: boolean;
  onCancel: () => void;
  onPreviousStep: () => void;
  parametersChanged: boolean;
  putToProduction: (callback: () => void) => void;
  isChatQueueType: boolean;
  removeFromProduction: () => void;
  shouldBlockLeave: boolean;
  updateAdjustments: (adjustments: Adjustment[]) => void;
  chartsTimezone: string;
  changeChartsTimezone: (timezone: string) => void;
  queueTimezone: string;
};

export type FormConfigureForecastProps = CommonFormConfigureForecastProps & {
  error: boolean;
  forecastData: CalculatedForecastData;
  loading: boolean;
};

export interface AdjustmentInChange {
  name?: string;
  type?: AdjustmentChangeType;
}

export const FormConfigureForecast: React.FC<FormConfigureForecastProps> = ({
  action,
  adjustments: initialAdjustments,
  createForecastAndPutToProduction,
  error,
  forecastData,
  form,
  handleSubmit,
  invalid,
  isEdit,
  isProduction,
  isView,
  loading,
  onCancel,
  onPreviousStep,
  parametersChanged,
  putToProduction,
  isChatQueueType,
  chartsTimezone,
  changeChartsTimezone,
  queueTimezone,
  removeFromProduction,
  shouldBlockLeave,
  updateAdjustments: updateInitialAdjustments,
  values,
}) => {
  const {
    name,
    forecastRange: { startDateTime, endDateTime },
    queueId,
  } = values;

  const { t } = useTranslation();
  const { isSidebarOpen, pinSidebar, openSidebar, toggleSidebar } = useSidebar();

  const [adjustmentsInCollision, setAdjustmentsInCollision] = useState<RecordBooleanType>({});
  const [adjustmentInChange, setAdjustmentInChange] = useState<AdjustmentInChange>({});
  const [adjustmentsRestored, setAdjustmentsRestored] = useState(false);
  const [currentAdjustmentRange, setCurrentAdjustmentRange] = useState<AdjustmentRangeType>({});
  const [previewedAdjustments, setPreviewedAdjustments] = useState<RecordBooleanType>({});
  const [preOpenedAdjustmentId, setPreOpenedAdjustmentId] = useState<string[]>([]);
  const [hoveredAdjustmentId, setHoveredAdjustmentId] = useState<string | null>(null);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);

  const forecastEndTime = createEndOfDateRange(endDateTime!);

  useEffect(() => {
    pinSidebar();
  });

  useEffect(() => {
    if (!loading) {
      if (error) {
        if (!isEmpty(adjustmentInChange)) {
          // Reset adjustmentInChange as calculation has failed.
          setAdjustmentInChange({});
        }
      } else if (!isEmpty(forecastData)) {
        if (!isEmpty(adjustmentInChange) && adjustmentInChange.type) {
          // Update local state when recalculation of adjustments was successful.
          toast.success(
            t(`forecast.adjustment${adjustmentInChangeLabelKey[adjustmentInChange.type]}Success`, {
              name: adjustmentInChange.name,
            })
          );
          setAdjustmentInChange({});
          resetCollisions();
          setAdjustments(initialAdjustments);
        } else if (!adjustments.length && !adjustmentsRestored) {
          // Store adjustments when retrieved from API for the first time.
          setAdjustments(initialAdjustments);
          setAdjustmentsRestored(true);
        } else if (!equals(initialAdjustments, adjustments)) {
          /**
           * Restore initialAdjustments with previous value of adjustments as it is containing adjustment
           * resolving in error on API during calculation.
           */
          updateInitialAdjustments(adjustments);
        }
      } else if (adjustmentsRestored && !equals(adjustments, initialAdjustments)) {
        /**
         * When adjustments sidebar get closed, result of calculation query looses error state and
         * provides result from previous call. If that error occurred within first call, we need to force
         * scene to provide another calculation with the adjustments stored in local state,
         * so the charts could be rendered with correct values, too.
         */
        updateInitialAdjustments(adjustments);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustmentInChange, error, forecastData, loading]);

  useEffect(() => {
    if (!isSidebarOpen) {
      resetCollisions();
      setPreOpenedAdjustmentId([]);
      setPreviewedAdjustments({});
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    if (preOpenedAdjustmentId.length) {
      if (!isSidebarOpen) {
        openSidebar();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preOpenedAdjustmentId.length ? preOpenedAdjustmentId.join('') : '']);

  const StartDatePickerComponent = DatePickerField({
    dataTestId: DATE_RANGE_START_DATE,
    disabled: true,
    label: t('date.dateFrom'),
  });

  const EndDatePickerComponent = DatePickerField({
    dataTestId: DATE_RANGE_END_DATE,
    disabled: true,
    label: t('date.dateTo'),
  });

  const renderedDateRange = (
    <>
      <Col className="forecasting-studio__create-forecast__date-range-title" data-test-id={DATE_RANGE_LABEL} sm="auto">
        {t('forecast.forecastRange')}:
      </Col>
      <Col className="forecasting-studio__create-forecast__date-range-wrapper" sm="auto">
        <Field name="forecastRange.startDateTime" component={StartDatePickerComponent} />
        <Field name="forecastRange.endDateTime" component={EndDatePickerComponent} />
      </Col>
    </>
  );

  const { testId, title, titleSlug } = getHeaderData(isEdit, isView);

  const onSubmitHandler = async (event: any) => {
    event.target.blur();
    event.preventDefault();

    await handleSubmit(event);
  };

  const evaluateCollisionsWithinAdjustments = (
    adjustmentStartDateTime: Date,
    adjustmentEndDateTime: Date,
    id?: string
  ) => {
    const start = zonedTimeToUtc(adjustmentStartDateTime, queueTimezone);
    const end = zonedTimeToUtc(adjustmentEndDateTime, queueTimezone);

    setCurrentAdjustmentRange({
      startDateTime: start.getTime(),
      endDateTime: end.getTime(),
    });

    if (!adjustmentStartDateTime || !adjustmentEndDateTime) {
      return;
    }

    const collisionAdjustmentIds = adjustments.reduce<RecordBooleanType>((ids, adjustment) => {
      if (
        adjustment.id !== id &&
        areIntervalsOverlapping(
          {
            start,
            end,
          },
          {
            start: new Date(adjustment.dateTimeRange.startDateTime).getTime(),
            end: new Date(adjustment.dateTimeRange.endDateTime).getTime(),
          }
        )
      ) {
        // eslint-disable-next-line no-param-reassign
        ids[adjustment.id] = true;
      }

      return ids;
    }, {});

    setAdjustmentsInCollision(collisionAdjustmentIds);
  };

  const resetCollisions = () => {
    setCurrentAdjustmentRange({});
    setAdjustmentsInCollision({});
  };

  const handleAdjustmentPreview = (id: string, opened: boolean) => {
    setPreviewedAdjustments({
      ...previewedAdjustments,
      [id]: opened,
    });
  };

  const previewAndCloseAllOtherAdjustments = (adjustmentId: string) => {
    setPreviewedAdjustments({ [adjustmentId]: true });
  };

  const handleAddNewAdjustment = (adjustment: Adjustment) => {
    const updatedAdjustments = handleRangeCollisionsWithinAdjustments(adjustment, adjustments, adjustmentsInCollision);
    updateInitialAdjustments(updatedAdjustments);
    setAdjustmentInChange({ name: adjustment.name, type: AdjustmentChange.CREATE });
  };

  const handleDeleteAdjustment = (adjustment: Adjustment) => {
    updateInitialAdjustments(adjustments.filter(({ id }) => id !== adjustment.id));
    setAdjustmentInChange({ name: adjustment.name, type: AdjustmentChange.DELETE });
  };

  const handleUpdateAdjustment = (adjustment: Adjustment) => {
    const updatedAdjustments = handleRangeCollisionsWithinAdjustments(adjustment, adjustments, adjustmentsInCollision);
    updateInitialAdjustments(updatedAdjustments);
    setAdjustmentInChange({ name: adjustment.name, type: AdjustmentChange.EDIT });
  };

  const handleUpdateProductionForecast = () => {
    if (isSidebarOpen) {
      toggleSidebar();
    }

    if (!isEdit) {
      createForecastAndPutToProduction(form, values);
    } else if (isProduction) {
      removeFromProduction();
    } else {
      const callback = parametersChanged ? handleSubmit : () => null;
      putToProduction(callback);
    }
  };

  const adjustmentsDisabled = isView || isProduction;

  const headerProps: HeaderProps = {
    testId,
    titleSlug,
    actionBar: (
      <Form onSubmit={onSubmitHandler}>
        <Row>
          <Col xs="auto">
            <Button
              color="primary"
              data-test-id={getButtonTestId(action, 2, PREVIOUS_STEP_BUTTON)}
              kind="icon"
              onClick={onPreviousStep}
              title={t('layout.steps.previous')}
            >
              <ArrowLeftIcon className="text-white" />
            </Button>
          </Col>
          <Separator />
          {!isView && (
            <Col xs="auto">
              <Button
                color="secondary"
                data-test-id={getButtonTestId(action, 2, SAVE_BUTTON)}
                // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
                disabled={invalid || isProduction}
                type="submit"
              >
                {t('layout.action.save')}
              </Button>
            </Col>
          )}
          <Col xs="auto">
            <Button
              className="button--icon--left-hand-side"
              color="info"
              data-test-id={getButtonTestId(action, 2, CANCEL_BUTTON)}
              onClick={onCancel}
            >
              <Icon color="#eb651c" kind="cross" size={10} />
              {t('layout.action.cancel')}
            </Button>
          </Col>
          <Separator />
          <div className="forecasting-studio__create-forecast__date-range-and-loader-wrapper">{renderedDateRange}</div>
          <Separator />
          <Col xs="auto">
            <Button
              // utility classes preventing broken position of icon and title when using icon size above 20
              className="button--icon--left-hand-side mr-2 py-0 h-100 my-auto"
              color={isProduction ? 'info' : 'secondary'}
              data-test-id={getButtonTestId(action, 2, PUT_TO_PRODUCTION_TOGGLE_BUTTON)}
              // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
              disabled={isView}
              onClick={handleUpdateProductionForecast}
            >
              <SharedIcon
                color={isProduction ? 'orange' : 'white'}
                kind={isProduction ? 'remove_from_production' : 'put_to_production'}
                size={24}
              />
              {t(`layout.action.${isProduction ? 'putToDraft' : 'putToProduction'}`)}
            </Button>
            <Button
              className="button--icon--right-hand-side"
              color="primary"
              data-test-id={getButtonTestId(action, 2, PROCEED_TO_SCHEDULE_BUTTON)}
              // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
              disabled={isView || !isProduction}
              tag={Link}
              to={{
                pathname: PATH_SCHEDULING_STUDIO_CREATE,
                state: {
                  initialScheduleInfo: {
                    queueIds: [queueId],
                    scheduleRange: {
                      startDateTime: startDateTime && zonedTimeToUtc(startDateTime, queueTimezone),
                      endDateTime: endDateTime && zonedTimeToUtc(createEndOfDateRange(endDateTime), queueTimezone),
                    },
                    timezone: queueTimezone,
                  },
                },
              }}
            >
              {t('schedule.proceedToScheduling')}
              <Icon color="white" kind="chevron-right" size={14} />
            </Button>
          </Col>
          <Col className="ml-auto" xs="auto">
            <Button
              active={isSidebarOpen}
              className="button--icon--left-hand-side"
              color="info"
              data-test-id={getButtonTestId(action, 2, ADJUSTMENTS_SIDEBAR_TOGGLE_BUTTON)}
              // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
              disabled={adjustmentsDisabled}
              onClick={toggleSidebar}
            >
              <SharedIcon color="#239ed8" kind="settings" size={20} />
              {t('forecast.adjustments')}
            </Button>
          </Col>
        </Row>
      </Form>
    ),
    title: t(title),
    icon: (
      <ProductionIcon
        isProduction={!isProduction}
        tooltipMessageKey={isProduction ? 'layout.states.inProduction' : 'layout.states.inDraft'}
        color={isProduction ? 'green' : 'orange'}
        size={26}
        testId={`forecast-icon:in-${isProduction ? 'production' : 'draft'}`}
      />
    ),
    subtitle: name,
  };

  return (
    <>
      <PromptModal
        t={t}
        when={shouldBlockLeave}
        onSave={async ({ proceed, close }) => {
          try {
            form.change(SKIP_SWITCH_TO_EDIT, true);
            await handleSubmit();
            proceed();
          } catch (e) {
            close();
          }
        }}
      />
      <Content
        header={headerProps}
        noMargin
        sidebarContent={
          <ForecastAdjustmentsSidebar
            addNewAdjustment={handleAddNewAdjustment}
            adjustments={adjustments}
            adjustmentsMutationInProgress={!isEmpty(adjustmentInChange)}
            concurrentChatsPresetValue={isChatQueueType ? values.concurrentChatsCount : null}
            containsError={!!error}
            containsRangeCollision={!isEmpty(adjustmentsInCollision)}
            deleteAdjustment={handleDeleteAdjustment}
            handleDateRangeChange={evaluateCollisionsWithinAdjustments}
            handleFormReset={resetCollisions}
            handlePreview={handleAdjustmentPreview}
            maxDate={forecastEndTime}
            minDate={new Date(startDateTime!)}
            preOpenedAdjustmentId={preOpenedAdjustmentId}
            queueTimezone={queueTimezone}
            updateAdjustment={handleUpdateAdjustment}
          />
        }
      >
        <Container className="container--wide-content">
          <LoadingOverlay loading={loading}>
            <div className="forecasting-studio__adjustments-panel">
              {adjustments?.length > 0 && (
                <AdjustmentsPanel
                  adjustments={adjustments}
                  adjustmentsInCollision={adjustmentsInCollision}
                  disabled={adjustmentsDisabled}
                  hoveredAdjustmentId={hoveredAdjustmentId}
                  previewAndCloseAllOtherAdjustments={previewAndCloseAllOtherAdjustments}
                  setHoveredAdjustmentId={setHoveredAdjustmentId}
                  setPreOpenedAdjustmentId={setPreOpenedAdjustmentId}
                  title={t('forecast.appliedAdjustments')}
                  timezone={queueTimezone}
                />
              )}
              <div className="ml-auto my-n1 py-1 pl-4 border-left border-white">
                <ForecastingDropdownTimezone
                  startDate={startDateTime!}
                  endDate={endDateTime!}
                  timezone={chartsTimezone}
                  queueTimezone={queueTimezone}
                  setTimezone={changeChartsTimezone}
                />
              </div>
            </div>
            {error ? null : (
              <Row className="forecasting-studio__charts-wrapper">
                <FormConfigureForecastCharts
                  adjustmentsInCollision={adjustmentsInCollision}
                  currentAdjustmentRange={currentAdjustmentRange}
                  endDateTime={forecastEndTime.getTime()}
                  forecastData={forecastData}
                  hoveredAdjustmentId={hoveredAdjustmentId}
                  previewedAdjustments={previewedAdjustments}
                  isSidebarOpen={isSidebarOpen}
                  timezone={chartsTimezone}
                />
              </Row>
            )}
          </LoadingOverlay>
        </Container>
      </Content>
    </>
  );
};
